import { css } from '@emotion/react';
import { ScheduleDefinition } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_definitions.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Accordion, Button, Chip, Text, Truncated } from '@frontend/design-system';
import { AvailabilityMode } from '../../../../components/AvailabilityRules';
import { OperatoryAvatar } from '../../../ManageOperatoryV3/operatory-avatar';
import { TransformedData } from '../../hooks/use-get-availability-rules';
import { AvailabilityTable } from './availability-table';

type Props = {
  availabilityMode: AvailabilityMode;
  entity: TransformedData;
  locationId: string;
  selectedScheduleDefinition: ScheduleDefinition | null;
  openAddEditTray: (scheduleDefinition: ScheduleDefinition | null, resourceId: string, isCopy?: boolean) => void;
};

export const AvailabilityRuleListContainer = ({
  selectedScheduleDefinition,
  openAddEditTray,
  availabilityMode,
  entity,
  locationId,
}: Props) => {
  const { t } = useTranslation('schedule-settings');
  const { isSmallMode } = useIsSmallMode();

  if (!entity) return null;

  return (
    <section css={containerStyles}>
      <header css={headerStyles(isSmallMode, !!entity.displayName)}>
        {availabilityMode === 'OPERATORY' && (
          <div css={avatarColumnStyles}>
            <OperatoryAvatar
              avatarInfo={entity?.operatoryIcon}
              operatoryName={entity.displayName || entity.externalName || ''}
              disabled={!entity.isActive}
            />
          </div>
        )}
        <div
          css={{
            gridColumn: '2',
            gridRow: '1',
            display: 'flex',
            flexDirection: isSmallMode ? 'column' : 'row',
            alignItems: 'center',
            gap: theme.spacing(1),
          }}
        >
          {entity.displayName && (
            <Truncated css={[textStyles(!!entity.isActive), displayNameStyles]}>{entity.displayName}</Truncated>
          )}
          {entity.externalName && (
            <Truncated css={[textStyles(!!entity.isActive), externalNameStyles(isSmallMode, !!entity.displayName)]}>
              {entity.externalName}
            </Truncated>
          )}
          {entity.sourceId && (
            <Chip
              variant='neutral'
              css={css`
                & span {
                  display: flex;
                  align-items: center;
                  gap: ${theme.spacing(0.5)};
                }
              `}
            >
              <Icon name='data-sync-small' css={{ marginRight: theme.spacing(0.5) }} />
              {/* TODO: Extract source name for sourceId */}
              {entity.sourceId}
            </Chip>
          )}
        </div>
      </header>
      {entity.isActive && (
        <Accordion variant='blank' chevronSize={24}>
          <Accordion.Item value='schedule definition templates'>
            <Accordion.Header
              css={{
                maxHeight: 40,
                '&[aria-expanded="true"]': { backgroundColor: theme.colors.neutral5 },
                '&:not([aria-expanded="true"])': { borderRadius: theme.borderRadius.medium },
              }}
            >
              {entity.scheduleDefinitions.length > 0 ? (
                <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Text size='medium' weight='bold'>
                    {t('Availability')}
                  </Text>
                  <Text size='small' color='subdued'>
                    {entity.count}
                  </Text>
                </div>
              ) : (
                <Chip variant='warn' css={{ minWidth: 'fit-content', backgroundColor: theme.colors.warning10 }}>
                  <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                    <Icon name='alert-filled-small' />
                    <Text size='medium'>{t('Availability:')}</Text>
                    <Text size='medium' weight='bold'>
                      {t('0')}
                    </Text>
                  </div>
                </Chip>
              )}
            </Accordion.Header>
            <Accordion.Body css={{ padding: 0 }}>
              <AvailabilityTable
                data={entity.scheduleDefinitions}
                availabilityMode={availabilityMode}
                locationId={locationId}
                selectedScheduleDefinition={selectedScheduleDefinition}
                openAddEditTray={openAddEditTray}
                entityId={entity.id}
              />
              <div css={{ padding: theme.spacing(1.5, 2) }}>
                <Button
                  variant='tertiary'
                  iconName='add'
                  size='small'
                  css={{ padding: theme.spacing(1, 0.25) }}
                  onClick={() => openAddEditTray(null, entity.id, true)}
                >
                  {t('Add Availability')}
                </Button>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
    </section>
  );
};

const containerStyles = {
  marginBottom: theme.spacing(4),
  border: `1px solid ${theme.colors.neutral30}`,
  borderRadius: theme.borderRadius.medium,
};

const headerStyles = (isSmallMode: boolean, hasDisplayName: boolean) =>
  css({
    display: 'grid',
    gridTemplateColumns: 'minmax(32px, 64px) auto',
    gridTemplateRows: hasDisplayName && isSmallMode ? 'auto auto' : 'auto',
    justifyContent: 'start',
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.colors.neutral20}`,
  });

const avatarColumnStyles = {
  display: 'flex',
  alignItems: 'center',
  gridRow: '1 / -1',
};

const displayNameStyles = {
  gridColumn: '2',
  gridRow: '1',
};

const externalNameStyles = (isSmallMode: boolean, hasDisplayName: boolean) => ({
  gridColumn: '2',
  gridRow: hasDisplayName && isSmallMode ? '2' : '1',
});

const textStyles = (isActive: boolean) => ({
  color: isActive ? theme.colors.neutral90 : theme.colors.neutral40,
  minWidth: 180,
  maxWidth: 240,
  marginLeft: theme.spacing(1),
});
